import OrderedMenuItem from "../OrderedMenuItem.js";

/**
 * Class holding all the actions for an editor, and providing helping methods
 * to handle them.
 *
 * @param {AbstractAction[]} actions - List of action spec to handle
 * @param {WysiwygStateManager} stateManager - StateManager holding thi actions manager
 * @param {function} [dropdownMenuItemBuilder] - Takes the dropdown
 *   options as argument (it must contain an `items` and a `label`
 *   fields). It should return an instance of a dropdown compatible
 *   with ProseMirror menu items. Every dropdown of ProseMirror's
 *   MenuBar will use the same builder. Defaults to
 *   `defaultDropdownItemBuilder` but can be used with
 *   `wrapAsProseMirrorDropdown` to use one of our own widgets
 */
export default class ActionsManager {
	constructor({ actions = [], stateManager, dropdownMenuItemBuilder }) {
		this._actions = actions;
		this._stateManager = stateManager;
		this._ensureDropdownMenuItemBuilder(dropdownMenuItemBuilder);
	}

	/**
	 * Return all the nodes provided by the plugins.
	 *
	 * @return {{[key: string]: ProseMirror.Node}}
	 */
	getNodes() {
		return this._actions.reduce(
			(nodes, action) => Object.assign(nodes, action.getNodes()),
			{},
		);
	}

	/**
	 * Return all the marks provided by the plugins.
	 *
	 * @return {{[key: string]: ProseMirror.Node}}
	 */
	getMarks() {
		return this._actions.reduce(
			(nodes, action) => Object.assign(nodes, action.getMarks()),
			{},
		);
	}

	/**
	 * Return all the nodeviews provided by the plugins.
	 *
	 * @return {{[key: string]: ProseMirror.NodeView}}
	 */
	getNodeViews() {
		return this._actions.reduce(
			(nodes, action) => Object.assign(nodes, action.getNodeViews()),
			{},
		);
	}

	/**
	 * Return all the plugins from the actions.
	 *
	 * @param {Schema} schema - Schema built by the WysiwygStateBuilder
	 * @return {ProseMirror.Plugin[]}
	 */
	getPlugins({ schema }) {
		return this._actions.reduce((acc, action) => {
			acc.push(...action.getPlugins({ actionsManager: this, schema }));

			return acc;
		}, []);
	}

	/**
	 * Retrieve the menu items from the actions, and sort them.
	 *
	 * @param {Schema} schema - Schema built by the WysiwygStateBuilder
	 * @param {OrderedMenuItem[]} [defaultMenuItems] - Menu items for the basic actions
	 *   provided by prosemirror
	 * @return {OrderedMenuItem[]}
	 */
	buildMenuItems({ schema, defaultMenuItems = [] }) {
		const orderedMenuItems = this._actions.reduce((acc, action) => {
			const menuItems = action.getMenuItems({
				schema,
				actionsManager: this,
			});
			if (!menuItems) {
				return acc;
			}

			acc.push(...menuItems);
			return acc;
		}, defaultMenuItems);

		orderedMenuItems.sort(OrderedMenuItem.sort);

		return orderedMenuItems.map((each) => each.toMenuItem());
	}

	replaceSelectionWithHTML(text) {
		return this._stateManager.replaceSelectionWithHTML(text);
	}

	replaceSelectionWithASTNode(node) {
		return this._stateManager.replaceSelectionWithASTNode(node);
	}

	replaceSelectionWithInputValue(inpuValue) {
		return this._stateManager.replaceSelectionWithInputValue(inpuValue);
	}

	_ensureDropdownMenuItemBuilder(dropdownMenuItemBuilder) {
		this._actions.forEach((action) =>
			action.ensureDropdownMenuItemBuilder(dropdownMenuItemBuilder),
		);
	}
}
